import type { OneOfExpression, Operation } from '@rtkwlf/querybroker';
import invariant from 'tiny-invariant';

import type { FieldBuilder } from './field-builder';
import { field } from './field-builder';
import type { ListBuilder } from './list-builder';
import { list } from './list-builder';
import { scalar, type ScalarBuilder } from './scalar-builder';

type Builder = OperationBuilder | FieldBuilder | ScalarBuilder | ListBuilder;
type Builders = Builder[];

export class OperationBuilder {
  #operator?: Operation['operator'];

  #qualifier?: Operation['qualifier'];

  #arguments?: Builders;

  #alias?: Operation['alias'];

  private insertFieldScalarArgs = (
    fieldObj: FieldBuilder,
    scalarObj: ScalarBuilder,
  ): void => {
    this.#arguments = [];
    this.#arguments.push(fieldObj);
    this.#arguments.push(scalarObj);
  };

  public and = (...operationBuilders: OperationBuilder[]): OperationBuilder => {
    this.#arguments = operationBuilders;
    this.#operator = 'and';
    return this;
  };

  public or = (...operationBuilders: OperationBuilder[]): OperationBuilder => {
    this.#arguments = operationBuilders;
    this.#operator = 'or';
    return this;
  };

  public countIf = (
    ...operationBuilders: OperationBuilder[]
  ): OperationBuilder => {
    this.#arguments = operationBuilders;
    this.#operator = 'countIf';
    return this;
  };

  public not = (operationBuilders: OperationBuilder): OperationBuilder => {
    this.#arguments = [operationBuilders];
    this.#operator = 'not';
    return this;
  };

  public timeInterval = (
    ...args: Array<OperationBuilder | FieldBuilder | ScalarBuilder>
  ): OperationBuilder => {
    this.#arguments = args;
    this.#operator = 'time_interval';
    return this;
  };

  public count = (
    ...operationBuilders: OperationBuilder[]
  ): OperationBuilder => {
    this.#arguments =
      operationBuilders.length === 0 ? undefined : operationBuilders;
    this.#operator = 'count';
    return this;
  };

  public sum = (...operationBuilders: FieldBuilder[]): OperationBuilder => {
    this.#arguments = operationBuilders;
    this.#operator = 'sum';
    return this;
  };

  /**
   *
   * eq represents "==" or "eq" operator in Query Broker
   */
  public eq = (
    fieldVal: string | FieldBuilder,
    scalarVal: string | number | ScalarBuilder,
  ): OperationBuilder => {
    this.insertFieldScalarArgs(
      typeof fieldVal !== 'object' ? field(fieldVal) : fieldVal,
      typeof scalarVal !== 'object' ? scalar(scalarVal) : scalarVal,
    );
    this.#operator = 'eq';
    return this;
  };

  public ne = (
    fieldVal: string | FieldBuilder,
    scalarVal: string | number | ScalarBuilder,
  ): OperationBuilder => {
    this.insertFieldScalarArgs(
      typeof fieldVal !== 'object' ? field(fieldVal) : fieldVal,
      typeof scalarVal !== 'object' ? scalar(scalarVal) : scalarVal,
    );
    this.#operator = 'ne';
    return this;
  };

  /**
   *
   * equal represents "==" or "eq" operator in Query Broker
   */
  public equal = this.eq;

  public in = (
    fieldVal: string | FieldBuilder,
    listValues: Array<string | number | ScalarBuilder>,
  ): OperationBuilder => {
    this.#arguments = [
      typeof fieldVal !== 'object' ? field(fieldVal) : fieldVal,
      list(listValues),
    ];
    this.#operator = 'in';
    return this;
  };

  public notIn = (
    fieldVal: string | FieldBuilder,
    listValues: Array<string | number | ScalarBuilder>,
  ): OperationBuilder => {
    this.#arguments = [
      typeof fieldVal !== 'object' ? field(fieldVal) : fieldVal,
      list(listValues),
    ];
    this.#operator = 'not_in';
    return this;
  };

  public greaterThan = (
    fieldObj: FieldBuilder,
    scalarObj: ScalarBuilder,
  ): OperationBuilder => {
    this.insertFieldScalarArgs(fieldObj, scalarObj);
    this.#operator = 'gt';
    return this;
  };

  public greaterThanEqual = (
    fieldObj: FieldBuilder,
    scalarObj: ScalarBuilder,
  ): OperationBuilder => {
    this.insertFieldScalarArgs(fieldObj, scalarObj);
    this.#operator = 'gte';
    return this;
  };

  public lessThan = (
    fieldObj: FieldBuilder,
    scalarObj: ScalarBuilder,
  ): OperationBuilder => {
    this.insertFieldScalarArgs(fieldObj, scalarObj);
    this.#operator = 'lt';
    return this;
  };

  public exists = (field: FieldBuilder): OperationBuilder => {
    this.#operator = 'exists';
    this.#arguments = [field];
    return this;
  };

  public lessThanEqual = (
    fieldObj: FieldBuilder,
    scalarObj: ScalarBuilder,
  ): OperationBuilder => {
    this.insertFieldScalarArgs(fieldObj, scalarObj);
    this.#operator = 'lte';
    return this;
  };

  public qualifier = (qualifier: Operation['qualifier']): OperationBuilder => {
    this.#qualifier = qualifier;
    return this;
  };

  public alias = (alias: Operation['alias']): OperationBuilder => {
    this.#alias = alias;
    return this;
  };

  public binary = (
    operator: string,
    fieldObj: FieldBuilder,
    scalarObj: ScalarBuilder,
  ) => {
    this.#operator = operator;
    this.insertFieldScalarArgs(fieldObj, scalarObj);
    return this;
  };

  toJSON = () => {
    invariant(this.#operator, 'Operator is not set');

    return {
      operation: {
        alias: this.#alias,
        arguments: this.#arguments,
        operator: this.#operator,
        qualifier: this.#qualifier,
      },
    };
  };

  build = (): OneOfExpression => {
    return JSON.parse(JSON.stringify(this));
  };
}

const operation = (...args: ConstructorParameters<typeof OperationBuilder>) =>
  new OperationBuilder(...args);

export { operation };
